import { CONTROL_FOCUS_CLASS, CONTROL_OPEN_CLASS } from "../lib/controlStyles";
import { ChevronDown, X } from "lucide-react";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Input } from "./FormControls";

import { cn } from "../lib/cn";
import {
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "./Menu";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Options sharing a group are listed under its heading, in the order the groups first appear. */
  group?: string;
  parentValue?: string;
}

/** A named set of options chosen together, such as "Everyone at NAGRA". */
export interface MultiSelectShortcut {
  label: string;
  values: readonly string[];
}

export interface MultiSelectProps {
  label: string;
  triggerLabel?: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** Shown when nothing is selected. */
  emptyLabel?: string;
  /** Shown when everything is selected. */
  allLabel?: string;
  disabled?: boolean;
  /**
   * Offer "Select all". Turn it off where choosing everything is never
   * intended, such as granting access to accounts.
   */
  selectAll?: boolean;
  allowEmpty?: boolean;
  searchable?: boolean;
  showSelection?: boolean;
  closeOnSelect?: boolean;
  /**
   * Sets of options toggled together, listed first. A shortcut whose options
   * are all chosen is checked and named in the summary in their place.
   */
  shortcuts?: readonly MultiSelectShortcut[];
  className?: string;
}

/**
 * Compact dropdown for choosing several values. The list stays open while
 * choosing, and the trigger summarizes the selection.
 */
export function MultiSelect({
  label,
  triggerLabel,
  options,
  value,
  onChange,
  emptyLabel = "None selected",
  allLabel = "All",
  disabled = false,
  selectAll = true,
  allowEmpty = true,
  searchable = false,
  showSelection = false,
  closeOnSelect = false,
  shortcuts = [],
  className,
}: MultiSelectProps) {
  const valueId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open || !searchable) return;
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, searchable]);
  const optionsByValue = new Map(options.map((option) => [option.value, option]));
  const isCovered = (option: MultiSelectOption) => {
    const visited = new Set<string>([option.value]);
    let parent = option.parentValue;
    while (parent && !visited.has(parent)) {
      if (value.includes(parent)) return true;
      visited.add(parent);
      parent = optionsByValue.get(parent)?.parentValue;
    }
    return false;
  };
  const chosen = options.filter((option) => value.includes(option.value) && !isCovered(option));
  const covered = shortcuts.filter(
    (shortcut) =>
      shortcut.values.length > 0 && shortcut.values.every((entry) => value.includes(entry)),
  );
  const coveredValues = new Set(covered.flatMap((shortcut) => shortcut.values));
  // A complete shortcut reads as its own name rather than the options it stands for.
  const named = [
    ...covered.map((shortcut) => shortcut.label),
    ...chosen.filter((option) => !coveredValues.has(option.value)).map((option) => option.label),
  ];
  const summary =
    chosen.length === 0
      ? emptyLabel
      : chosen.length === options.length
        ? allLabel
        : `${named.slice(0, 2).join(", ")}${named.length > 2 ? "…" : ""}`;
  const selectionTitle = named.length > 0 ? named.join(", ") : emptyLabel;
  const filtered = options.filter((option) =>
    `${option.label} ${option.group ?? ""}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const groups = [...new Set(filtered.map((option) => option.group ?? ""))];
  // Keep the options' own order so the stored selection is stable.
  const commit = (next: Set<string>) =>
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));

  return (
    <div className="min-w-0 space-y-2">
      <Menu
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          setQuery("");
        }}
      >
        <MenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            aria-label={triggerLabel ?? label}
            aria-describedby={valueId}
            className={cn(
              "group density-control-md flex w-full items-center justify-between gap-2 rounded-control border border-control-border bg-surface px-3 text-sm text-primary transition-colors",
              "hover:border-control-border-hover",
              CONTROL_OPEN_CLASS,
              CONTROL_FOCUS_CLASS,
              "disabled:cursor-not-allowed disabled:bg-surface-elevated disabled:text-muted disabled:opacity-70",
              className,
            )}
          >
            <span id={valueId} title={selectionTitle} className="min-w-0 flex-1 truncate text-left">
              {summary}
            </span>
            <ChevronDown
              className="size-3.5 shrink-0 text-muted transition-transform group-data-[state=open]:rotate-180 motion-reduce:transition-none"
              aria-hidden
            />
          </button>
        </MenuTrigger>
        <MenuContent
          align="start"
          collisionPadding={8}
          className={cn(
            "max-h-72 w-(--radix-dropdown-menu-trigger-width) min-w-56 overflow-y-auto",
            searchable && "pt-0",
          )}
        >
          {searchable && (
            <div className="sticky top-0 z-10 -mx-1 bg-surface px-3 pb-2 pt-3">
              <Input
                ref={searchRef}
                type="search"
                aria-label={`Search ${label.toLowerCase()}`}
                placeholder="Search…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    const results = event.currentTarget
                      .closest('[role="menu"]')
                      ?.querySelectorAll<HTMLElement>('[role="menuitemcheckbox"]');
                    const index = event.key === "ArrowDown" ? 0 : (results?.length ?? 0) - 1;
                    results?.[index]?.focus();
                  }
                  if (event.key !== "Escape" && event.key !== "Tab") event.stopPropagation();
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </div>
          )}
          {selectAll ? (
            <MenuItem
              onSelect={(event) => {
                if (!closeOnSelect) event.preventDefault();
                commit(new Set(options.map((option) => option.value)));
              }}
            >
              Select all
            </MenuItem>
          ) : null}
          {allowEmpty ? (
            <MenuItem
              onSelect={(event) => {
                if (!closeOnSelect) event.preventDefault();
                commit(new Set());
              }}
            >
              Clear
            </MenuItem>
          ) : null}
          {!query && shortcuts.length > 0
            ? shortcuts.map((shortcut) => {
                const checked = covered.includes(shortcut);
                return (
                  <MenuCheckboxItem
                    key={shortcut.label}
                    indicatorStyle="checkbox"
                    className={cn(
                      "min-h-9 py-1.5 font-medium",
                      checked && "bg-focus/10 text-interaction-text focus:bg-focus/15",
                    )}
                    checked={checked}
                    onSelect={(event) => {
                      if (!closeOnSelect) event.preventDefault();
                    }}
                    onCheckedChange={(next) => {
                      const values = new Set(value);
                      for (const entry of shortcut.values)
                        if (next) values.add(entry);
                        else values.delete(entry);
                      commit(values);
                    }}
                  >
                    <span className="min-w-0 break-words">{shortcut.label}</span>
                  </MenuCheckboxItem>
                );
              })
            : null}
          <MenuSeparator />
          {groups.map((group) => (
            <Fragment key={group}>
              {group ? <MenuLabel>{group}</MenuLabel> : null}
              {filtered
                .filter((option) => (option.group ?? "") === group)
                .map((option) => (
                  <MenuCheckboxItem
                    key={option.value}
                    indicatorStyle="checkbox"
                    className={cn(
                      "min-h-9 py-1.5",
                      option.parentValue && "pl-8",
                      option.parentValue &&
                        optionsByValue.get(option.parentValue)?.parentValue &&
                        "pl-12",
                      (value.includes(option.value) || isCovered(option)) &&
                        "bg-focus/10 text-interaction-text focus:bg-focus/15",
                    )}
                    checked={value.includes(option.value) || isCovered(option)}
                    disabled={
                      isCovered(option) ||
                      (!allowEmpty && chosen.length === 1 && value.includes(option.value))
                    }
                    onSelect={(event) => {
                      if (!closeOnSelect) event.preventDefault();
                    }}
                    onCheckedChange={(checked) => {
                      const next = new Set(value);
                      if (checked) next.add(option.value);
                      else next.delete(option.value);
                      commit(next);
                    }}
                  >
                    <span className="min-w-0 break-words">{option.label}</span>
                  </MenuCheckboxItem>
                ))}
            </Fragment>
          ))}
          {filtered.length === 0 && <p className="px-3 py-4 text-sm text-muted">No matches</p>}
        </MenuContent>
      </Menu>
      {showSelection && chosen.length > 0 && (
        <ul aria-label={`Selected ${label.toLowerCase()}`} className="flex flex-wrap gap-2">
          {chosen.map((option) => (
            <li
              key={option.value}
              className="flex min-w-0 max-w-full items-center gap-1 rounded-control bg-surface-elevated py-1 pl-2 text-xs text-secondary"
            >
              <span className="min-w-0 break-words">{option.label}</span>
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                title={`Remove ${option.label}`}
                disabled={disabled}
                className={cn(
                  "touch-target grid size-6 shrink-0 place-items-center rounded-control hover:bg-surface-sunken disabled:opacity-50",
                  CONTROL_FOCUS_CLASS,
                )}
                onClick={() => commit(new Set(value.filter((entry) => entry !== option.value)))}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
